/**
 * Amazon Advertising Integration Module (Story 19.13)
 *
 * Barrel export for OAuth, REST helpers, errors, and the Amazon Ads adapter.
 *
 * Amazon Ads characteristics worth knowing about before reading this code:
 *  - OAuth 2.0 + PKCE against Login with Amazon (LWA) endpoints
 *    (`https://www.amazon.com/ap/oa` for authorize,
 *     `https://api.amazon.com/auth/o2/token` for token).
 *  - **HTTP Basic Authentication** on the token endpoint (matches Pinterest
 *    19.4 + Reddit 19.10, diverges from Snapchat 19.7 / MS Ads 19.1 /
 *    Klaviyo). `client_id` / `client_secret` go in the Authorization header,
 *    NOT the body.
 *  - **DOUBLE-COLON scope** (`advertising::campaign_management`) — single
 *    colon triggers `unknown_scope` per amzn/ads-advanced-tools-docs
 *    Discussion #161.
 *  - **Refresh tokens DO NOT rotate** — refresh response OMITS the field;
 *    adapter carries forward `credentials.refreshToken` unchanged.
 *  - **Multi-region profile partitioning (NA / EU / FE)** — discovery
 *    requires sequential calls to all 3 regional `/v2/profiles` endpoints
 *    (NOT `Promise.all` — preserves LWA quota fairness).
 *  - Aggressive 60-minute access TTL — `inlineRefreshOn401: true` is
 *    REQUIRED by the TD4 invariant (access TTL < 24h).
 *  - **180-day client-secret rotation** enforced by Amazon at the LWA app
 *    level. Refresh tokens survive rotation as long as `client_id` is
 *    unchanged.
 *  - **AC #15 highest-blast-radius invariant:** LWA `invalid_client` →
 *    `deployment_misconfig` (NOT `token_expired`). Mismapping triggers a
 *    customer-support avalanche on a single server-side typo.
 *  - **Stories 19.14 / 19.15 / 19.16 / 19.17 ship the metric pipeline** —
 *    19.13 ships the adapter shell with empty metric/dimension catalogues.
 *
 * The folder is named `amazon/` (not `amazon-ads/`) to leave room for future
 * Amazon properties (Amazon Attribution beta, Amazon Marketplace Web Service,
 * SP-API for selling-side data), mirroring `pinterest/` / `microsoft/` /
 * `snapchat/` / `reddit/`.
 */

import { registerAdapter } from "../registry";
import { amazonAdsAdapter } from "./amazon-ads";

// Register the adapter so getAdapter("amazon-ads") returns it.
registerAdapter("amazon-ads", amazonAdsAdapter);

// Platform values (shared constant for frontend/backend alignment)
export const AMAZON_ADS_PLATFORM_VALUES = ["amazon-ads"] as const;
export type AmazonAdsPlatform = (typeof AMAZON_ADS_PLATFORM_VALUES)[number];

// OAuth utilities
export {
  buildAmazonAdsAuthUrl,
  exchangeAmazonAdsCode,
  refreshAmazonAdsToken,
  parseAmazonAdsCallbackError,
  validateTokenResponse,
  validateScope,
  extractOAuthErrorCode,
  extractOAuthErrorDescription,
  buildBasicAuthHeader,
  sanitizeAmazonAdsErrorMessage,
  AMAZON_ADS_SCOPES,
  AMAZON_ADS_REGIONAL_HOSTS,
  AmazonAdsTokenError,
  type AmazonAdsTokenErrorOptions,
  type AmazonAdsAuthUrlOptions,
  type AmazonAdsTokenResponse,
  type AmazonAdsCallbackError,
  type AmazonAdsRegion,
} from "./oauth";

// REST helpers
export {
  buildAmazonAdsHeaders,
  fetchAmazonAdsProfilesForRegion,
  fetchAmazonAdsProfilesAcrossRegions,
  buildAmazonAdsApiError,
  discoverDspAdvertisersForProfile,
  AMAZON_ADS_REGION_DISCOVERY_TIMEOUT_MS,
  type AmazonAdsAccountInfo,
  type AmazonAdsProfile,
  type AmazonAdsProfilesResult,
  type RegionalProfilesResult,
  type RegionStatus,
  type AmazonAdsDspAdvertiser,
  type AmazonAdsDspDiscoveryResult,
} from "./api";

// Error parsing
export {
  parseAmazonAdsApiError,
  amazonAdsErrorTypeToAdapterCode,
  isApiNotEnabledError,
  isDspNotEnabledError,
  isInvalidClientError,
  AmazonAdsApiCallError,
  type AmazonAdsApiError,
  type AmazonAdsErrorType,
} from "./errors";

// Adapter
export {
  amazonAdsAdapter,
  AMAZON_ADS_METRICS,
  AMAZON_ADS_DIMENSIONS,
  AMAZON_ADS_API_VERSION,
  validateAmazonAdsSpDryRunProbe,
} from "./amazon-ads";

// Sponsored Products v3 reports module (Story 19.14, Task 4)
export {
  AMAZON_ADS_REPORT_API_CONTENT_TYPE,
  AMAZON_ADS_S3_HOSTNAME_REGEX,
  AMAZON_ADS_SP_CACHE_MAX_RECORDS,
  AMAZON_ADS_SP_CACHE_TTL_FRESH_SEC,
  AMAZON_ADS_SP_CACHE_TTL_FINALIZED_SEC,
  AMAZON_ADS_SP_MAX_DAYS_PER_REPORT,
  AMAZON_ADS_SP_MAX_DECOMPRESSED_BYTES,
  AMAZON_ADS_SP_MAX_REPORTS_PER_PROFILE,
  AMAZON_ADS_SP_POLL_CADENCE_MS,
  AMAZON_ADS_SP_REPORT_TIMEOUT_MS,
  AMAZON_ADS_SP_TERMINAL_SUCCESS_STATUSES,
  AMAZON_ADS_SP_PENDING_STATUSES,
  applyDerivedMetrics,
  buildAmazonAdsSpCacheKey,
  buildSpReportRequest,
  cancelReport,
  computeAmazonAdsSpCacheTtl,
  computeRequestHash,
  detectColumnDrift,
  downloadAndParseReport,
  isSafeS3DownloadUrl,
  normalizeRequestForHash,
  parseSpNdjsonRecord,
  pollReport,
  submitSponsoredProductsReport,
  validateCachePayload,
  SpReportApiError,
  type SpReportConfig,
  type SpReportRecord,
  type SpReportRequestBody,
  type SpSubmitReportArgs,
  type SpSubmitReportResult,
  type SpPollReportResult,
  // Story 19.15 — multi-product extensions
  buildAmazonAdsCacheKey,
  buildAmazonAdsReportRequest,
  computeAmazonAdsCacheTtl,
  parseAmazonAdsNdjsonRecord,
  parseAmazonAdsReportApiError,
  pickAmazonAdsReportType,
  submitAmazonAdsReport,
  validateAdProductReportTypePair,
  type AmazonAdsAdProduct,
  type AmazonAdsReportTypeId,
  type AnyAmazonAdsReportRequestBody,
  type SbReportRequestBody,
  type SdReportRequestBody,
  type PickAmazonAdsReportTypeResult,
  type BuildAmazonAdsReportRequestArgs,
} from "./reports";

export {
  AMAZON_ADS_SP_METRICS,
  AMAZON_ADS_SP_DIMENSIONS,
  SP_REPORT_TYPE_IDS,
  SP_V3_COLUMN_MAP,
  SP_V3_DIMENSION_MAP,
  SP_V2_TO_V3_RENAME_GOLDEN,
  pickReportType,
  buildColumnList,
  buildGroupByList,
  type SpReportTypeId,
  type SpMetricColumnEntry,
  type SpDimensionColumnEntry,
} from "./sp-column-map";

// Orchestration entry point (consumed by adapter + queue workers)
export {
  runSpFetchMetrics,
  type SpReportJobsApi,
  type SpKvApi,
  type RunSpFetchMetricsInput,
} from "./sp-fetch-metrics";

export {
  buildSpReportJobsApi,
  buildSpKvApi,
} from "./sp-report-jobs-d1";

// Story 19.15 — Sponsored Brands
export {
  AMAZON_ADS_SB_DIMENSIONS,
  AMAZON_ADS_SB_METRICS,
  SB_REPORT_TYPE_IDS,
  SB_V3_COLUMN_MAP,
  SB_V3_DIMENSION_MAP,
  applySbDerivedMetrics,
  buildSbColumnList,
  buildSbGroupByList,
  pickSbReportType,
  type SbReportTypeId,
  type SbMetricColumnEntry,
  type SbDimensionColumnEntry,
} from "./sb-column-map";

export {
  AMAZON_ADS_SB_DATA_FRESHNESS_LAG_HOURS,
  AMAZON_ADS_SB_MAX_DAYS_PER_REPORT,
  AMAZON_ADS_SB_REPORT_TIMEOUT_MS,
  runSbFetchMetrics,
  type RunSbFetchMetricsInput,
} from "./sb-fetch-metrics";

// Story 19.15 — Sponsored Display
export {
  AMAZON_ADS_SD_DIMENSIONS,
  AMAZON_ADS_SD_METRICS,
  SD_REPORT_TYPE_IDS,
  SD_V3_COLUMN_MAP,
  SD_V3_DIMENSION_MAP,
  buildSdColumnList,
  buildSdGroupByList,
  pickSdReportType,
  type SdReportTypeId,
  type SdMetricColumnEntry,
  type SdDimensionColumnEntry,
} from "./sd-column-map";

export {
  AMAZON_ADS_SD_DATA_FRESHNESS_LAG_HOURS,
  AMAZON_ADS_SD_MAX_DAYS_PER_REPORT,
  AMAZON_ADS_SD_REPORT_TIMEOUT_MS,
  runSdFetchMetrics,
  type RunSdFetchMetricsInput,
} from "./sd-fetch-metrics";

// Story 19.15 — shared report orchestration
export {
  AMAZON_ADS_MAX_REPORTS_PER_INTEGRATION,
  AMAZON_ADS_REPORT_TIMEOUT_MS,
  runAmazonAdsReportPipeline,
  type AmazonAdsKvApi,
  type AmazonAdsReportJobsApi,
  type RunAmazonAdsReportPipelineInput,
  type RunAmazonAdsReportPipelineResult,
} from "./shared/report-orchestration";

// Story 19.15 — adapter dispatch helper
export { groupMetricsByAdProduct } from "./amazon-ads";

// ============================================================================
// Story 19.16 — DSP (Demand-Side Platform)
// ============================================================================

export {
  AMAZON_ADS_DSP_DIMENSIONS,
  AMAZON_ADS_DSP_METRICS,
  DSP_REPORT_TYPE_IDS,
  DSP_V3_COLUMN_MAP,
  DSP_V3_DIMENSION_MAP,
  buildDspColumnList,
  buildDspGroupByList,
  getDspReportTypeIds,
  pickDspReportType,
  registerDspExtendedColumns,
  type DspReportTypeId,
  type DspMetricColumnEntry,
  type DspDimensionColumnEntry,
  type DspExtendedColumnSource,
  type PickDspReportTypeResult,
} from "./dsp-column-map";

export {
  AMAZON_ADS_DSP_DATA_FRESHNESS_LAG_HOURS,
  AMAZON_ADS_DSP_MAX_DAYS_PER_REPORT,
  AMAZON_ADS_DSP_REPORT_TIMEOUT_MS,
  pickDspAccountId,
  runDspFetchMetrics,
  type PickDspAccountIdResult,
  type RunDspFetchMetricsInput,
} from "./dsp-fetch-metrics";

export {
  AMAZON_ADS_DSP_REPORT_API_CONTENT_TYPE,
  AMAZON_ADS_DSP_S3_HOSTNAME_REGEX,
  AMAZON_ADS_DSP_TYPE_BY_REPORT_TYPE_ID,
  AMAZON_ADS_PENDING_STATUSES_BY_PRODUCT,
  AMAZON_ADS_TERMINAL_SUCCESS_STATUSES_BY_PRODUCT,
  AMAZON_ADS_FINALISATION_WINDOW_HOURS,
  AMAZON_ADS_AD_PRODUCT_SLUG,
  AMAZON_ADS_2026_ATTRIBUTION_BOUNDARY_MS,
  AMAZON_ADS_ALLVIEWS_COLUMNS_BY_PRODUCT,
  shouldEmitAllViewsBaseline,
  getAllViewsBaselineColumns,
  downloadAndParseDspReport,
  isDspReportRequestBody,
  parseDspJsonRecord,
  submitAmazonAdsDspReport,
  type DspParseRecordResult,
  type DspReportRequestBody,
  type DownloadAndParseDspResult,
} from "./reports";

// ============================================================================
// Story 19.16.1 — runtime introspection + extended DSP column maps + STORE_ANALYTICS
// ============================================================================

export {
  AMAZON_ADS_DSP_AUDIENCE_FLOOR_DIMENSIONS,
  AMAZON_ADS_DSP_AUDIENCE_FLOOR_METRICS,
  AMAZON_ADS_DSP_AUDIENCE_SIZE_ENUM,
  AMAZON_ADS_DSP_COLUMN_NAME_REGEX,
  AMAZON_ADS_DSP_INTROSPECTED_SCHEMA_TTL_SEC,
  buildAudienceSchemaCacheKey,
  compareAudienceSchemaForDrift,
  introspectAudienceSchema,
  isValidAffinityIndex,
  isValidAudienceSize,
  loadIntrospectedAudienceSchema,
  type AudienceFloorDimensionName,
  type AudienceFloorMetricName,
  type AudienceSchemaDrift,
  type AudienceSizeEnumValue,
  type IntrospectAudienceSchemaArgs,
  type IntrospectAudienceSchemaResult,
} from "./dsp-audience-introspection";

export {
  AMAZON_ADS_DSP_AUDIO_VIDEO_AUDIO_FORMATS,
  AMAZON_ADS_DSP_AUDIO_VIDEO_CREATIVE_TYPES,
  AMAZON_ADS_DSP_AUDIO_VIDEO_FLOOR_DIMENSIONS,
  AMAZON_ADS_DSP_AUDIO_VIDEO_FLOOR_METRICS,
  AMAZON_ADS_DSP_AUDIO_VIDEO_VIDEO_FORMATS,
  buildAudioVideoSchemaCacheKey,
  compareAudioVideoSchemaForDrift,
  introspectAudioVideoSchema,
  loadIntrospectedAudioVideoSchema,
  validateAudioVideoEnumValue,
  type AudioVideoFloorDimensionName,
  type AudioVideoFloorMetricName,
  type AudioVideoSchemaDrift,
} from "./dsp-audio-video-introspection";

export {
  AMAZON_ADS_DSP_TECHNICAL_MAX_DAYS_PER_REPORT,
  DSP_TECHNICAL_COLUMN_MAP,
  TECHNICAL_KNOWN_ENUM_VALUES,
  validateTechnicalEnumValue,
  type TechnicalEnumField,
} from "./dsp-technical-column-map";

export {
  DSP_FUNNEL_COLUMN_COUNT,
  DSP_FUNNEL_COLUMN_MAP,
  DSP_FUNNEL_EVENT_NAMES,
  DSP_FUNNEL_EVENT_SUFFIXES,
  type DspFunnelEventName,
  type DspFunnelEventSuffix,
} from "./dsp-funnel-events-column-map";

export {
  DSP_MASHUP_COLUMN_COUNT,
  DSP_MASHUP_COLUMN_MAP,
} from "./dsp-mashup-column-map";

export {
  AMAZON_ADS_STORE_ANALYTICS_DIMENSIONS,
  AMAZON_ADS_STORE_ANALYTICS_ENDPOINT_PATH,
  AMAZON_ADS_STORE_ANALYTICS_MAX_DAYS_PER_REPORT,
  AMAZON_ADS_STORE_ANALYTICS_METRICS,
  BSE_COLUMN_COUNT,
  BSE_LEVELS,
  BSE_PER_LEVEL_METRICS,
  STORE_ANALYTICS_REPORT_TYPE_IDS,
  STORE_ANALYTICS_SECTION_LEVEL_COLUMN_MAP,
  type BseLevel,
  type BsePerLevelMetric,
  type StoreAnalyticsMetricColumnEntry,
  type StoreAnalyticsReportTypeId,
} from "./store-analytics-column-map";

export {
  AMAZON_ADS_DSP_MAX_DAYS_BY_REPORT_TYPE,
} from "./dsp-fetch-metrics";

export {
  AMAZON_ADS_SP_PROMPT_TEXT_MAX_LENGTH,
  truncateSpPromptText,
} from "./sp-column-map";

export {
  AMAZON_ADS_SB_PROMPT_TEXT_MAX_LENGTH,
  truncateSbPromptText,
} from "./sb-column-map";
